import { useEffect } from 'react'
import { Sparkles } from 'lucide-react'
import { Button } from '@highlight/ui'
import { GlobalEvents } from '@highlight/ipc'
import { useGlobalStore, useIsIpcReady } from '@highlight/ipc/react'
import { START_CHAT_EVENT, type NewChatEventPayload } from '@renderer/chat/integrations/conversation-action'
import { usePricingEdition } from '@renderer/chat/hooks/use-plan-features'
import { useUnifiedBilling } from '@renderer/chat/store/global-billing'
import { useConnections } from '@renderer/hooks/use-connections'
import { getUsableIntegrationProviderIds } from '@renderer/hooks/use-integration-connections'
import { useOpenSettingsTab } from '@renderer/overlay/Sidepanel/utils/use-open-settings-tab'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { SKILLS_SETTINGS_TAB, SUBSCRIPTION_SETTINGS_TAB } from '@shared/settings/settings-deeplink'
import { NotchToastRow } from '../components/notch-toast-row'
import { Toast } from '../components/toast'
import { toastManager } from '../toast-store'
import { useToast } from '../use-toast'
import { customSkillSuggestionFor } from './custom-skill-suggestion'
import {
  PLAN_LIVE_TOAST_ID,
  PLAN_LIVE_TOAST_SEEN_KEY,
  decidePlanLiveToast,
  type PlanLiveToastVariant,
} from './plan-live-toast-rule'

/** Billing persists across launches, so only a snapshot fetched since this renderer started reflects the current plan. */
const RENDERER_STARTED_AT = performance.timeOrigin

type ToastApi = ReturnType<typeof useToast>

interface PlanLiveCopy {
  title: string
  /** The notch row's quieter clause after the title. Max has none. */
  detail?: string
  /** The corner card's line under the title. */
  description: string
  /** The corner card's button. */
  cta: string
  /** Names the click in analytics. */
  control: string
}

const COPY: Record<PlanLiveToastVariant, PlanLiveCopy> = {
  pro: {
    title: 'Your new Pro plan is live',
    detail: 'Try a custom skill',
    description: 'Custom skills are now included. Build one around how you work.',
    cta: 'Try a custom skill',
    control: 'try custom skill',
  },
  'pro-from-lite': {
    title: 'Welcome to Pro',
    detail: 'Your skills and daily recaps are ready',
    description: 'Your skills and daily recaps are ready.',
    cta: 'See your skills',
    control: 'see skills',
  },
  max: {
    title: 'Your new Max limits are live',
    description: 'Your plan now includes more credits and meeting hours each month.',
    cta: 'View your plan',
    control: 'view plan',
  },
}

/**
 * Tells a user who was already paying when the pricing revamp landed, once, that it has landed
 * on their plan. Watches the flag and billing stores, so it fires when the flag flips under a
 * running app as well as on the first launch after.
 */
export function usePlanLiveToast(toast: ToastApi) {
  const isIpcReady = useIsIpcReady()
  const edition = usePricingEdition()
  const { subscription, billingProfile, lastUpdated } = useUnifiedBilling()
  const { isPending: connectionsPending } = useConnections()
  const [hasSeen, setHasSeen] = useGlobalStore<boolean>('user', PLAN_LIVE_TOAST_SEEN_KEY, false)
  const previousPlan = billingProfile?.previousPlan
  const billingFresh = lastUpdated >= RENDERER_STARTED_AT

  useEffect(() => {
    const decision = decidePlanLiveToast({
      isIpcReady,
      billingFresh,
      connectionsSettled: !connectionsPending,
      edition,
      plan: subscription.plan,
      previousPlan,
      hasSeen,
    })
    if (!decision) return
    if (decision.action === 'mark-seen') {
      setHasSeen(true)
      return
    }
    const { variant } = decision

    toast.add({
      analytics: { type: 'plan live', variant },
      id: PLAN_LIVE_TOAST_ID,
      icon: <Sparkles size={16} />,
      title: COPY[variant].title,
      persistent: true,
      autoExpandDelayMs: 0,
      priority: 1,
      notchRow: (toastId) => <PlanLiveNotchRow toastId={toastId} variant={variant} />,
      body: (toastId) => <PlanLiveToastCard toastId={toastId} variant={variant} />,
    })

    // The store drops toasts until onboarding completes, so one that never showed stays unseen.
    const shown = toastManager.getToasts().some((t) => t.id === PLAN_LIVE_TOAST_ID && !t.exiting)
    if (shown) setHasSeen(true)
  }, [
    isIpcReady,
    billingFresh,
    connectionsPending,
    edition,
    subscription.plan,
    previousPlan,
    hasSeen,
    toast,
    setHasSeen,
  ])
}

/** What the click does by tier: Pro gets a custom skill to try, a Lite migrant its skills, Max its new limits. */
function usePlanLiveAction(toastId: string, variant: PlanLiveToastVariant) {
  const { data: connections } = useConnections()
  const openSettingsTab = useOpenSettingsTab()

  return () => {
    const properties: Record<string, unknown> = { action: COPY[variant].control, variant }
    switch (variant) {
      case 'pro': {
        const suggestion = customSkillSuggestionFor(getUsableIntegrationProviderIds(connections))
        const payload: NewChatEventPayload = { prompt: suggestion.prompt, sourceLabel: 'Home' }
        GlobalEvents.emit(START_CHAT_EVENT, payload)
        properties.suggestion = suggestion.kind
        break
      }
      case 'pro-from-lite':
        openSettingsTab(SKILLS_SETTINGS_TAB)
        break
      case 'max':
        openSettingsTab(SUBSCRIPTION_SETTINGS_TAB)
        break
    }
    AmplitudeRenderer.trackEvent('Plan Live Toast', properties)
    toastManager.dismiss(toastId)
  }
}

function PlanLiveNotchRow({ toastId, variant }: { toastId: string; variant: PlanLiveToastVariant }) {
  const act = usePlanLiveAction(toastId, variant)
  const { title, detail, control } = COPY[variant]

  return <NotchToastRow control={control} title={detail ? `${title}.` : title} detail={detail} onClick={act} />
}

function PlanLiveToastCard({ toastId, variant }: { toastId: string; variant: PlanLiveToastVariant }) {
  const act = usePlanLiveAction(toastId, variant)
  const { description, cta, control } = COPY[variant]

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        <Toast.Description>{description}</Toast.Description>
        <Toast.Actions>
          <Button size="sm" variant="primary" data-notch-control={control} onClick={act}>
            {cta}
          </Button>
        </Toast.Actions>
      </Toast.Content>
    </Toast>
  )
}
